import { memo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import MenuIcon from '@mui/icons-material/MoreHoriz';
import ErrorIcon from '@mui/icons-material/Error';
import _startsWith from 'lodash/startsWith';
import _pick from 'lodash/pick';
//Local files
import { Root, Wrapper, Paper, Text, Channel, Rejected, Time, Approve, Info, Actions, MenuButton, Img } from './Message.styled';
import BaseAvatar from 'components/Common/BaseAvatar/BaseAvatar';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useMessages from 'hooks/useMessages';
import useSuccess from 'hooks/useSuccess';
import { messagesSelectors } from 'features/messages';
import { conversationsSelectors } from 'features/conversations';
import { linkify, getRepresentation } from 'helpers';
import { representationVariants } from 'helpers/constants';
import { DELETE_MESSAGE } from 'helpers/confirmations';

const Message = ({ message: { id, text, medias, sender, channel, status, created_at, hasError = false } }) => {
  const messageRef = useRef(null);
  const { openFilePreviewDialog, openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { approveMessage, rejectMessage } = useMessages();
  const { mode } = useCustomSelector(conversationsSelectors.getConversation);
  const processing = useCustomSelector(messagesSelectors.messageIsFetching);
  const [menuEl, setMenuEl] = useState(null);
  const own = sender === 'manager';
  const time = new Date(created_at).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true, month: '2-digit', day: '2-digit', year: 'numeric' });
  const sendersForReject = sender === 'bot' || sender === 'system';
  const sendersForApprove = sendersForReject || sender === 'patient';
  const isApproveMode = (mode === 'approve' || mode === 'gate') && sendersForApprove && status === 'newest';
  const isNeedToReject = (mode === 'approve' || mode === 'gate') && sendersForReject && status === 'newest';

  const handleApprove = () => {
    approveMessage(id)
    .then(({ error }) => {
      if (!error) {
        setSuccess('Message successfully approved');
      }
    })
  };
  const handleReject = () => {
    rejectMessage(id)
    .then(({ error }) => {
      if (!error) {
        setSuccess('Message successfully rejected');
      }
    })
  };
  const handleDelete = () => {
    openDeleteDialog({ id, type: DELETE_MESSAGE });
    setMenuEl(null);
  }

  return (
    <Root id={`message-${id}`} ref={messageRef} sender={sender}>
      <Wrapper sender={sender}>
      {((sender === 'bot' && mode === 'manual') || sender === 'system') && <Info><Text sender={sender}>Not visible to the patient</Text></Info>}
        <Paper sender={sender}>
          {!!medias?.length && (
            <Box>
              {_startsWith(medias[0].content_type, 'image/') && (
                <ButtonBase onClick={() => openFilePreviewDialog(_pick(medias[0], ['filename', 'representations', 'content_type', 'localUrl'])) }>
                  <Img alt='attachment' src={getRepresentation(medias[0].representations, representationVariants.SMALL)} />
                </ButtonBase>
              )}
            </Box>
          )}
          <Text sender={sender} sx={{ wordBreak: 'break-word' }}>{linkify(text)}</Text>
          {sender !== 'manager' && <Channel sender={sender}>{channel}</Channel>}
          {status === 'rejected' && <Rejected><Text sender={sender}>Rejected</Text></Rejected>}
        </Paper>
        {time !== 'pending' && (
          <Actions sender={sender}>
            {isApproveMode && <Approve>
              {isNeedToReject && <Button size='small' color='error' variant='outlined' disabled={processing} onClick={handleReject}>Reject</Button>}
              <Button size='small' color='primary' variant='outlined' disabled={processing} onClick={handleApprove} sx={mode === 'gate' && sender === 'patient' && { p: 0 }}>Approve</Button>
            </Approve>}
            {own && <>
              <MenuButton onClick={(event) => setMenuEl(event.currentTarget)}><MenuIcon /></MenuButton>
              <Popover
                open={!!menuEl}
                anchorEl={menuEl}
                onClose={() => setMenuEl(null)}
              >
                <Button sx={{ width: 90 }} color='error' onClick={handleDelete}>Delete</Button>
              </Popover>
            </>}
            <Box display='flex'>
              { hasError && <ErrorIcon  color='error' sx={{ marginRight: '5px' }} /> }
              <Time sender={sender}>{time}</Time>
            </Box>
          </Actions>
        )}
      </Wrapper>
      {sender === 'patient' && (
        <BaseAvatar
          key={sender}
          src={''}
          userable={sender}
          message
        />
      )}
    </Root>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <Message {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);